import { useCallback } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { ResolvedInvoiceStatusDto } from '@tiendanube/common/src/domains/orders/commons';
import { useAppDispatch } from 'App/store';
import { UpdateInvoiceEventType } from '../../ordersService';
import {
  invoiceRequestById,
  resolveInvoices,
  updateInvoiceById,
} from '../../ordersSlice/orderSlice';

interface InterfaceOrderInvoices {
  sendInvoiceRequest: () => void;
  handleInvoiceSubmission: () => void;
  getResolvedInvoices: () => Promise<ResolvedInvoiceStatusDto>;
  handleInvoiceOperation: (
    operationType: UpdateInvoiceEventType,
  ) => Promise<void>;
  fetchResolvedInvoices: (
    setStatus: (value: ResolvedInvoiceStatusDto | null) => void,
  ) => Promise<void>;
  handleConfirmInvoiceOperation: (
    operationType: UpdateInvoiceEventType,
    setStatus: (value: ResolvedInvoiceStatusDto | null) => void,
  ) => Promise<void>;
  operationReason: string;
}

interface useOrderInvoicesProps {
  orderId: string;
  fulfillmentOrderId: string;
  operationReason: string;
  onInvoiceError?: () => void;
  onInvoiceRequestSuccess?: () => void;
  onInvoiceOperationSuccess?: (operationType: UpdateInvoiceEventType) => void;
  onInvoiceOperationError?: (operationType: UpdateInvoiceEventType) => void;
  onLoading?: () => void;
}

function useOrderInvoices({
  orderId,
  fulfillmentOrderId,
  operationReason,
  onInvoiceError,
  onInvoiceRequestSuccess,
  onInvoiceOperationSuccess,
  onInvoiceOperationError,
  onLoading,
}: useOrderInvoicesProps): InterfaceOrderInvoices {
  const dispatch = useAppDispatch();

  const sendInvoiceRequest = useCallback(async () => {
    unwrapResult(
      await dispatch(invoiceRequestById({ orderId, fulfillmentOrderId })),
    );
  }, [dispatch, orderId, fulfillmentOrderId]);

  const handleInvoiceSubmission = useCallback(async () => {
    try {
      await sendInvoiceRequest();
      onInvoiceRequestSuccess?.();
    } catch (error) {
      onInvoiceError?.();
    }
  }, [sendInvoiceRequest, onInvoiceError, onInvoiceRequestSuccess]);

  const sendInvoiceOperation = useCallback(
    async (operationType: UpdateInvoiceEventType): Promise<void> => {
      unwrapResult(
        await dispatch(
          updateInvoiceById({
            orderId,
            fulfillmentOrderId,
            reason: operationReason,
            eventType: operationType,
          }),
        ),
      );
    },
    [dispatch, orderId, fulfillmentOrderId, operationReason],
  );

  const handleInvoiceOperation = useCallback(
    async (operationType: UpdateInvoiceEventType) => {
      try {
        onLoading?.();
        await sendInvoiceOperation(operationType);
        onInvoiceOperationSuccess?.(operationType);
      } catch (error) {
        console.info('Error en handleInvoiceOperation:', error);
        onInvoiceOperationError?.(operationType);
      }
    },
    [
      sendInvoiceOperation,
      onInvoiceOperationError,
      onInvoiceOperationSuccess,
      onLoading,
    ],
  );

  const getResolvedInvoices = useCallback(
    async (): Promise<ResolvedInvoiceStatusDto> =>
      await dispatch(resolveInvoices({ orderId, fulfillmentOrderId })).unwrap(),
    [dispatch, orderId, fulfillmentOrderId],
  );

  const fetchResolvedInvoices = useCallback(
    async (setStatus: (value: ResolvedInvoiceStatusDto | null) => void) => {
      const result = await getResolvedInvoices();
      setStatus(result);
    },
    [getResolvedInvoices],
  );

  const handleConfirmInvoiceOperation = async (
    operationType: UpdateInvoiceEventType,
    setStatus: (value: ResolvedInvoiceStatusDto | null) => void,
  ): Promise<void> => {
    await handleInvoiceOperation(operationType);
    await fetchResolvedInvoices(setStatus);
  };

  return {
    sendInvoiceRequest,
    handleInvoiceSubmission,
    getResolvedInvoices,
    handleInvoiceOperation,
    fetchResolvedInvoices,
    handleConfirmInvoiceOperation,
    operationReason,
  };
}

export default useOrderInvoices;
